import React, { useState, useEffect } from 'react';
import { Box, Heading, Text } from '@landr/maestro';
import { ExperimentFeaturedSavingPlanVariationFlagEnum, PlanFrequencyEnum } from '@landr/core.models';
import { FeaturesTable } from '@landr/pricing';
import { useFlag } from 'context/flagsProvider';
import { FeatureFlag } from 'types';
import { PricingTabDocument } from '../types';
import { usePricingContext } from '../common/PricingContext';
import { getTabDataByUID, isPricingTabOfTypeDistribution } from '../common/helpers';
import { StyledPricingTabContent, StyledPricingTabPlans } from '../styled';
import { PricingPlan } from './PricingPlan';
import '../Pricing.scss';

const frequencySwitch = (currentFequency: PlanFrequencyEnum) => {
    return currentFequency === PlanFrequencyEnum.Yearly ? PlanFrequencyEnum.Monthly : PlanFrequencyEnum.Yearly;
};

const PlanFrequencyToggleRequired = {
    Monthly: false,
    Yearly: false,
    Both: true,
};

const checkFrequencyToggleRequired = (tabData: '' | PricingTabDocument | null) => {
    return tabData && PlanFrequencyToggleRequired[(tabData as PricingTabDocument).frequencies_available];
};

export type PricingTabProps = { tab: PricingTabDocument; languageCode: string };

export const PricingTab: React.FC<PricingTabProps> = ({ tab, languageCode }) => {
    const { pricingTabsData, translations } = usePricingContext();
    const currentPricingTabData = getTabDataByUID(pricingTabsData, tab._meta.uid);
    const [frequency, setFrequency] = useState(PlanFrequencyEnum.Yearly);
    const [isFrequencyToggleAvailable, setIsFrequencyToggleAvailable] = useState(false);
    const [isDistributionTab, setIsDistributionTab] = useState(false);
    const [yearlySaving, setYearlySaving] = useState(0);
    const experimentFeaturedSavingPlanVariationFlag = useFlag([FeatureFlag.ExperimentFeaturedSavingPlan]);

    let experimentVariation = 'Original';
    if (
        experimentFeaturedSavingPlanVariationFlag &&
        experimentFeaturedSavingPlanVariationFlag.experiment &&
        experimentFeaturedSavingPlanVariationFlag.experiment.variation
    ) {
        experimentVariation = experimentFeaturedSavingPlanVariationFlag.experiment.variation;
    }

    useEffect(() => {
        if (currentPricingTabData) {
            if (checkFrequencyToggleRequired(currentPricingTabData)) {
                // If the service offer in the state is Mastering, enable the frequency toggle
                setIsFrequencyToggleAvailable(true);
            }
            setIsDistributionTab(isPricingTabOfTypeDistribution(currentPricingTabData));
        }
    }, [currentPricingTabData]);

    return (
        <>
            <Heading>
                <Box paddingY="xl" display="flex" justifyContent="center" flexDirection={'column'}>
                    {isFrequencyToggleAvailable && (
                        <>
                            <Box display="flex" justifyContent="center">
                                <Text size={'lg'} paddingRight={'lg'}>
                                    {translations.toggle_monthly}
                                </Text>
                                <Box width="75px">
                                    {/* // TODO ECO-1938: replace with maestro toggle component */}
                                    <Box className="PricingTable-frequency Switch">
                                        <input
                                            type="checkbox"
                                            id="frequency-switch"
                                            aria-label="frequency-toggle"
                                            checked={frequency === PlanFrequencyEnum.Yearly}
                                            onClick={() => {
                                                setFrequency(state => {
                                                    const nextFrequency = frequencySwitch(state);
                                                    // TODO: DIS-627 Implement pricing routing
                                                    // updatePath(currentPricingTabData._meta.uid, nextFrequency);
                                                    return nextFrequency;
                                                });
                                            }}
                                            onChange={() => null}
                                        />

                                        <label aria-label={`toggle-${frequency}`} htmlFor="frequency-switch"></label>
                                    </Box>
                                </Box>
                                <Text size={'lg'} paddingLeft={'lg'}>
                                    {translations.toggle_yearly}
                                </Text>
                            </Box>
                            {/* TODO: Change color on A/B testing featuredFlag MAS-1957 */}
                            {experimentVariation !== 'Original' && (
                                <Box display="flex" justifyContent="center" mt={15}>
                                    <Text
                                        color={
                                            experimentVariation ===
                                            ExperimentFeaturedSavingPlanVariationFlagEnum.Variation2
                                                ? 'danger.500'
                                                : 'primary.500'
                                        }
                                    >
                                        <b>
                                            {translations.save_up_to} {yearlySaving}%
                                        </b>{' '}
                                        {translations.on_yearly_plans}
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Heading>
            {currentPricingTabData && (
                <StyledPricingTabContent aria-label="pricing-tab" display="flex" flexDirection="column">
                    <StyledPricingTabPlans>
                        {currentPricingTabData.plans.map(({ plan }) => {
                            return (
                                <PricingPlan
                                    key={plan.name}
                                    plan={plan}
                                    frequency={frequency}
                                    setYearlySaving={setYearlySaving}
                                    languageCode={languageCode}
                                    experimentVariation={experimentVariation}
                                ></PricingPlan>
                            );
                        })}
                    </StyledPricingTabPlans>
                    {isDistributionTab && (
                        <Box paddingTop={'jb'}>
                            <FeaturesTable />
                        </Box>
                    )}
                </StyledPricingTabContent>
            )}
        </>
    );
};
